import React from 'react';
import Markdown, { Components } from 'react-markdown';
import { useHistory } from 'react-router-dom';
import { usePatronSelector } from '../../common/use-patron-selector';
import {
  isInternalUrl,
  isLegacyLink,
  convertLegacyAssistantUrl,
} from '../../lib/url-utils';
import { Link } from '../ui';
import useAssistantActionHandlers from '../../common/use-assistant-action-handlers';
import { NavigationReferer } from '../assistant/assistant-action';
import { AssistantLink } from '../assistant/assistant-link';

interface MarkdownLinkProps {
  href?: string;
  children: React.ReactNode;
}

/**
 * MarkdownLink component handles different types of links in chatbot markdown:
 * 1. Assistant links (modern and legacy SC4) - routed through AssistantLink
 * 2. Legacy SC4 hash links - converted to path-based navigation
 * 3. Regular internal links - routed with history.push
 * 4. External links - opened in new tab
 */
export const MarkdownLink: React.FC<MarkdownLinkProps> = ({
  href,
  children,
}) => {
  const program = usePatronSelector((state) => state.program);
  const history = useHistory();
  const { getDeeplink } = useAssistantActionHandlers();

  if (!href) {
    return (
      <Link href="" className="chatbot__markdown-link">
        {children}
      </Link>
    );
  }

  // Check if this is a legacy SC4 assistant URL and convert it
  const convertedUrl = convertLegacyAssistantUrl(href);
  const finalUrl = convertedUrl ?? href;

  const isInternal = isInternalUrl(finalUrl, {
    rootPath: program.root_path,
    isCustomDomain: program.is_custom_domain,
  });

  // Check if this is an assistant URL (either converted from legacy or modern format)
  const isAssistantLink =
    convertedUrl !== null ||
    finalUrl.includes('/assistant/') ||
    finalUrl.includes('#assistant/');

  // Handle assistant links with AssistantLink component
  if (isInternal && isAssistantLink) {
    try {
      // Use getDeeplink to determine the proper navigation for assistant URLs
      const deeplink = getDeeplink({ url: finalUrl, type: 'deeplink' } as any, {
        referer: NavigationReferer.INBOX,
      });

      return (
        <AssistantLink
          to={deeplink.to ?? deeplink.url}
          className="chatbot__markdown-link"
        >
          {children}
        </AssistantLink>
      );
    } catch (error) {
      console.error('Error handling assistant link:', error);
    }
  }

  // Handle click for internal non-assistant links, especially legacy sc4 links
  const handleClick = isInternal
    ? (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!finalUrl) return;

        try {
          const url = new URL(finalUrl, window.location.origin);
          const isLegacy = isLegacyLink(url);

          if (isLegacy) {
            // Convert legacy sc4 hash links to path-based links
            // e.g., /sc4#service/4/command/... -> /sc4/service/4/command/...
            e.preventDefault();
            const path = `/sc4${url.hash}${url.search}`;
            history.push(path);
          }
          // For non-legacy internal links, the action="push" prop handles navigation
        } catch (error) {
          console.error('Error handling link click:', error);
        }
      }
    : undefined;

  // For non-assistant internal links and external links, use regular Link
  return (
    <Link
      href={finalUrl}
      target={isInternal ? undefined : '_blank'}
      action={isInternal ? 'push' : undefined}
      onClick={handleClick}
      className="chatbot__markdown-link"
    >
      {children}
    </Link>
  );
};

interface ChatbotMarkdownProps {
  content: string;
  className?: string;
}

/**
 * Reusable markdown renderer for chatbot messages and AI summaries.
 * Supports:
 * - Headings (h1-h4)
 * - Lists (ordered and unordered)
 * - Text formatting (bold, italic)
 * - Links (internal and external, including legacy SC4 assistant links)
 * - Code blocks and inline code
 * - Blockquotes
 * - Horizontal rules
 */
const ChatbotMarkdown: React.FC<ChatbotMarkdownProps> = ({
  content,
  className = 'chatbot__message-markdown',
}) => {
  const markdownComponents = {
    p: ({ children }) => <p className="chatbot__markdown-p">{children}</p>,
    ul: ({ children }) => <ul className="chatbot__markdown-ul">{children}</ul>,
    ol: ({ children }) => <ol className="chatbot__markdown-ol">{children}</ol>,
    li: ({ children }) => <li className="chatbot__markdown-li">{children}</li>,
    strong: ({ children }) => (
      <strong className="chatbot__markdown-strong">{children}</strong>
    ),
    em: ({ children }) => <em className="chatbot__markdown-em">{children}</em>,
    h1: ({ children }) => <h1 className="chatbot__markdown-h1">{children}</h1>,
    h2: ({ children }) => <h2 className="chatbot__markdown-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="chatbot__markdown-h3">{children}</h3>,
    h4: ({ children }) => <h4 className="chatbot__markdown-h4">{children}</h4>,
    blockquote: ({ children }) => (
      <blockquote className="chatbot__markdown-blockquote">
        {children}
      </blockquote>
    ),
    code: ({ children }) => (
      <code className="chatbot__markdown-code">{children}</code>
    ),
    pre: ({ children }) => (
      <pre className="chatbot__markdown-pre">{children}</pre>
    ),
    a: ({ children, href }) => (
      <MarkdownLink href={href}>{children}</MarkdownLink>
    ),
    hr: () => <hr className="chatbot__markdown-hr" />,
  } as Components;

  return (
    <div className={className} data-testid="playwright-chatbot-markdown">
      <Markdown components={markdownComponents}>{content}</Markdown>
    </div>
  );
};

export default ChatbotMarkdown;
